import { useCallback, useEffect, useState } from "react"

import { sentFiles } from "./acknowledgeUploads"

const SLOTS = ["cover", "description", "narration"]
const isFile = (value) => typeof File !== "undefined" && value instanceof File

function savedEntry(file) {
  return { file, name: file.name, status: "saved" }
}

export function useBundleUploadStatus(files = {}) {
  const [entries, setEntries] = useState(() => Object.fromEntries(
    SLOTS.filter((slot) => files[slot]?.name).map((slot) => {
      const file = files[slot]
      return [slot, isFile(file) ? { file, name: file.name, status: "waiting" } : savedEntry(file)]
    })
  ))

  useEffect(() => {
    setEntries((current) => {
      let next = current
      const change = (slot, entry) => {
        if (next === current) next = { ...current }
        if (entry) next[slot] = entry
        else delete next[slot]
      }

      for (const slot of SLOTS) {
        const file = files[slot]
        const entry = current[slot]
        if (!file) {
          if (entry) change(slot, null)
        } else if (isFile(file)) {
          if (entry?.file !== file) change(slot, { file, name: file.name, status: "waiting" })
        } else if (file.name && !(entry?.status === "saved" && entry.name === file.name)) {
          change(slot, savedEntry(file))
        }
      }
      return next
    })
  }, [files])

  const noteFileChange = useCallback((slot, file) => {
    setEntries((current) => {
      if (!file) {
        if (!current[slot]) return current
        const next = { ...current }
        delete next[slot]
        return next
      }
      return {
        ...current,
        [slot]: isFile(file)
          ? { file, name: file.name, status: "waiting" }
          : savedEntry(file),
      }
    })
  }, [])

  const transitionSent = useCallback((snapshot, payload, status) => {
    const sent = sentFiles(snapshot, payload).bundle
    if (!sent.length) return
    setEntries((current) => {
      let next = current
      for (const [slot, file] of sent) {
        if (current[slot]?.file !== file) continue
        if (next === current) next = { ...current }
        next[slot] = { ...current[slot], status }
      }
      return next
    })
  }, [])

  const statuses = Object.fromEntries(Object.entries(entries).map(([slot, entry]) => [slot, entry.status]))

  return {
    statuses,
    noteFileChange,
    uploadStarted: useCallback((snapshot, payload) => transitionSent(snapshot, payload, "uploading"), [transitionSent]),
    uploadSaved: useCallback((snapshot, payload) => transitionSent(snapshot, payload, "saved"), [transitionSent]),
    uploadFailed: useCallback((snapshot, payload) => transitionSent(snapshot, payload, "failed"), [transitionSent]),
  }
}
